import PhoneInputWithCountry from "react-phone-number-input";
import type { ComponentPropsWithRef } from "react";
import { useFieldId } from "../useFieldId";
import { cn } from "@/lib/utils";
import Label from "../Label";
import { FieldError, fieldErrorId } from "../FieldError";
import { FieldHint, fieldHintId } from "../FieldHint";
import { fieldControlClass } from "../field-variants";
import { type ContactVerifyStatus, verifyStatusBorderClass } from "../verifyStatus";
import { PhoneCountrySelect } from "./PhoneCountrySelect";
import type { CountryCode } from "./types";

export type PhoneInputProps = Omit<
  ComponentPropsWithRef<"input">,
  "value" | "onChange" | "type"
> & {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  defaultCountry: CountryCode;
  placeholder?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  verifyStatus?: ContactVerifyStatus;
};

export function PhoneInput({
  label,
  value,
  onChange,
  defaultCountry,
  placeholder,
  error,
  hint,
  required,
  verifyStatus,
  disabled,
  id,
  name,
  ref,
  className: _phoneClassName,
  ...inputRest
}: PhoneInputProps) {
  const inputId = useFieldId(id, name);
  const errorId = inputId ? fieldErrorId(inputId) : undefined;
  const hintId = inputId ? fieldHintId(inputId) : undefined;
  const statusBorder = verifyStatusBorderClass(verifyStatus, Boolean(error));

  return (
    <div className="w-full">
      {label ? (
        <Label htmlFor={inputId} spacing="field">
          {label}
          {required ? <span className="text-error-500"> *</span> : null}
        </Label>
      ) : null}
      <PhoneInputWithCountry
        id={inputId}
        international
        countryCallingCodeEditable
        defaultCountry={defaultCountry}
        value={value || undefined}
        onChange={(next) => onChange(next ?? "")}
        disabled={disabled}
        countrySelectComponent={PhoneCountrySelect}
        countrySelectProps={{
          error: Boolean(error),
          verifyStatus,
        }}
        className={cn(
          "PhoneInput flex w-full items-stretch gap-2",
          error && "PhoneInput--error",
        )}
        numberInputProps={{
          ...inputRest,
          ref,
          name,
          placeholder,
          className: fieldControlClass({
            className: cn("PhoneInputInput flex-1", statusBorder),
          }),
          "aria-invalid": error ? "true" : "false",
          "aria-describedby": error ? errorId : hint ? hintId : undefined,
        }}
      />
      {error && errorId ? (
        <FieldError id={errorId}>{error}</FieldError>
      ) : null}
      {hint && !error && hintId ? (
        <FieldHint id={hintId}>{hint}</FieldHint>
      ) : null}
    </div>
  );
}

